/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {getJobErrorText} from '@ion/applications/src/components/jobs/helpers';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJob} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';

// defines the DownloadJobRerunPrompt Prop type definitions ...
export interface DownloadJobRerunPromptProps {
  show: boolean;
  taskId?: number;
  downloadjob: DownloadJob;
  result?: DownloadJob;
  loading?: boolean;
  error?: ServiceError;
  isRerunFailed?: boolean;
  rerunFailedShow?: boolean;
  rerunFailedTaskShow?: boolean;
  setIsRerunFailed?: Function;
  onRerunConfirm: (downloadjob: DownloadJob) => void;
  onRerunCancel: () => void;
}

const useStyles = makeStyles(() => ({
  actionLabel : {
    minWidth : '120px'
  },
  message : {
    paddingTop : 10
  },
  jobName : {
    fontWeight : 500
  }
}));

/**
 * @DownloadJobRerunPrompt
 *
 * The DownloadJobRerunPrompt provides the UI for confirmation of rerunning a Job.
 *
 * ```typescript
 *
 *    import {DownloadJobRerunPrompt} from './DownloadJobRerunPrompt';
 *
 *      <DownloadJobRerunPrompt show={isEditing}
 *        downloadjob={selected}
 *        onRerunConfirm={onRerunConfirm}
 *        onRerunCancel={onRerunCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the job rerun prompt will be displayed.
 * @param {DownloadJob}   downloadjob The selected job that is to be rerun.
 * @param {DownloadJob}   result The job returned by the service after it has been rerun.
 * @param {boolean}       loading When true, indicates that the job is currently being
 *                        rerun.
 * @param {ServiceError}  error A reference to the ServiceError if the rerun operation fails.
 * @param {Function}      onRerunConfirm callback function which will be invoked with the
 *                        job when the user confirms the rerun operation.
 * @param {Function}      onRerunCancel callback function which will be invoked if the user
 *                        cancels the rerun operation.
 */
export const DownloadJobRerunPrompt = ({
  show,
  downloadjob,
  result,
  loading,
  error,
  taskId = 0,
  onRerunConfirm,
  onRerunCancel,
  rerunFailedTaskShow,
  setIsRerunFailed,
  isRerunFailed = false,
  rerunFailedShow = false
}: DownloadJobRerunPromptProps) => {
  const classes                       = useStyles();
  const [showError, setShowError]     = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setShowError(!!error);
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowSuccess(true);
    }
  }, [result]);

  const onAction = useCallback(function() {
    onRerunConfirm(downloadjob);
  }, [downloadjob]);

  const onDismiss = useCallback(function() {
    onRerunCancel();
  }, []);

  const onClose = useCallback(function() {
    setShowError(false);
    setShowSuccess(false);
    setIsRerunFailed?.(false);
  }, []);

  const getMessage = (name, filename, count = 0) => {
    if (rerunFailedShow || rerunFailedTaskShow) {
      const failedMsg = rerunFailedShow
        ? translate('downloadjobs:rerun.messageFailed', {name: `<span class=${classes.jobName}>${name}</span>`, devicesCount: `<span class=${classes.jobName}>${count}</span>`})
        : translate('downloadjobs:rerun.taskMessageFailed', {name: `<span class=${classes.jobName}>${filename}</span>`, devicesCount: `<span class=${classes.jobName}>${count}</span>`});
      const text = `
      <div>
        ${failedMsg}
      </div>
      <div class=${classes.message}>
        ${translate(`downloadjobs:rerun.${rerunFailedShow ? 'messageFailed1' : 'messageFailed2'}`)}
      </div>`;
      return <div dangerouslySetInnerHTML={{__html: text}} />;
    }
    return translate('downloadjobs:rerun.message', {name});
  };

  if (show || showError || showSuccess) {
    const name = downloadjob?.name.length ? downloadjob.name : translate('downloadjobs:rerun.thisJob');
    const filename = rerunFailedTaskShow ? downloadjob?.tasks?.[taskId]?.metadata.title : '';
    const deviceCount = rerunFailedTaskShow
      ? downloadjob?.tasks?.[taskId]?.counts.failed
      : downloadjob?.details?.failed;
    return (
      <>
        {show && (
          <UserNotificationDialog show={show}
            capitalize
            title={translate(`downloadjobs:rerun.${rerunFailedShow ? 'titleFailed': rerunFailedTaskShow ? 'titleTaskFailed' :'title'}`)}
            message={getMessage(name, filename, deviceCount)}
            actionLabel={
              <span className={classes.actionLabel}>
                {
                  !loading ? (
                    translate('downloadjobs:rerun.confirm')
                  ) : <ButtonSpinner />
                }
              </span>
            }
            dismissLabel={translate('downloadjobs:rerun.cancel')}
            onAction={onAction}
            onDismiss={onDismiss} />
        )}
        {showError && (
          <IonSnackbar show={showError}
            message={`${translate('downloadjobs:rerun.failure')} ${getJobErrorText(error, true)}`}
            actionLabel={translate('downloadjobs:rerun.ok')}
            onAction={onClose}
            onClose={onClose} />
        )}
        {showSuccess && (
          <IonSnackbar show={showSuccess}
            message={translate(`downloadjobs:rerun.${isRerunFailed ? 'successFailed' : 'success'}`)}
            actionLabel={translate('downloadjobs:rerun.ok')}
            onAction={onClose}
            onClose={onClose}
            autoHideDuration={3000} />
        )}
      </>
    );
  }
  return null;
};

export default DownloadJobRerunPrompt;
